<template>
    <TMobileItems
        v-model="sortingValue"
        :footer-button-text="footerButtonText"
        :sorting-buttons="sortingPanelButtons"
        :active-buttons="indexPanelActiveButtons"
        :current-letter-index-panel="currentLetterIndexPanel"
        :index-panel="!isLandscape"
        :theme="'back'"
        :is-controls="false"
        :color="categoryColor"
        :is-footer="isLandscape"
        sorting-panel
        class="o-mobile-care mobile-care"
        @onFooterButton="handleClickBack"
        @clickLetter="handleClickLetter"
    >
        <template #header>
            <OMobileSearchHeader
                :title="categoryName"
                :subtitle="categoryDescription"
            >
                <template #rightIcon>
                    <div class="o-mobile-care-header-icons">
                        <!--
                        <svg-icon
                            original
                            name="icons/m-search-setting"
                            class="o-mobile-care-header-icons__setting"
                        />
                        -->
                        <img
                            v-if="categoryIcon && !isLandscape"
                            :src="categoryIcon"
                            alt="logo image"
                            class="o-mobile-care-header-icons__logo"
                        />
                        <svg-icon
                            v-else
                            original
                            name="icons/search_mobile"
                            class="o-mobile-care-header-icons__search"
                        />
                    </div>
                </template>
            </OMobileSearchHeader>
            <div
                class="m-header__line"
                :style="`background-color: ${categoryColor}`"
            />
        </template>
        <template #container>
            <RecycleScroller
                ref="tilesList"
                #default="{ item, index }"
                :items="items"
                :item-size="isLandscape ? 38 :67"
                class="mobile-care__scroller"
                :class="{'mobile-care__scroller--landing': !isLandscape}"
                emitUpdate
                @update="highlightActiveLetter"
            >
                <OWItemSearchTile
                    v-if="t(item, 'type') === 'tenant' || t(item, 'type') === 'service'"
                    :item="item"
                    @onClick="handleTileClick"
                    :style="`background-color: ${index%2 == 0|| index == 0 ? colorConvert(categoryColorCode) : '#fff'};`"
                />
                <OWItemSearchCategoryTile
                    v-else
                    :item="item"
                    :style="`background-color: ${index%2 == 0|| index == 0 ? colorConvert(categoryColorCode) : '#fff'};`"
                    @onClick="handleTileClick"
                />
            </RecycleScroller>
        </template>
    </TMobileItems>
</template>

<script>
import { 
    mapState, 
    mapGetters  
} from 'vuex';
import uniq from "lodash/uniq";

import TMobileItems from '../../templates/t-mobile-items/t-mobile-items';
import OWItemSearchTile from '../../organisms/o-w-item-search-tile/o-w-item-search-tile';
import OMobileSearchHeader from '../../organisms/o-mobile-search-header/o-mobile-search-header';
import OWItemSearchCategoryTile from '../../organisms/o-w-item-search-category-tile/o-w-item-search-category-tile';

import { GUIDE_NEW_POINT } from "../../../utils/constants/bus-events-constants";
import {
    MOBILE_ROOT,
    MOBILE_CARE,
    MOBILE_GUIDE,
    MOBILE_CATEGORY,
} from '../../../utils/constants/pages';

import {colorConvert} from '../../../utils/helpers/colorConvert';

export default {
    name: 'OMobileCare',
    components: {
        TMobileItems,
        OWItemSearchTile,
        OMobileSearchHeader,
        OWItemSearchCategoryTile
    },
    data() {
        return {
            sortingValue: 'All',
            currentLetterIndexPanel: 'a',
            scrollerItemsNumber: 0, // This variable shows us how many items be in RecycleScroller
        }
    },
    props: {
        /**
         * Use in case we need to exclude some item from tenants list.
         * For example: items on searchbar on guide screen
         */
        filteredByItem: {
            type: Number,
            default: null
        },
        isCloseButtonShow: {
            type: Boolean,
            default: false
        },
        categoryId:{
            type: Number,
            default: null,
        }
    },
    computed: {
        ...mapState({
            isLandscape: state => state.landscape
        }),
        ...mapGetters([
            't',
            'getTenantsAZ',
            'getObjectsByCategoryAZ',
            'getListItem',
        ]),
        categoryIdInt(){
            return this.$route.params.categoryId ? parseInt(this.$route.params.categoryId) : null;
        },
        category(){
            return this.categoryIdInt ? this.getListItem(this.categoryIdInt) : null;
        },
        /**
         * Filtered tenants by item (if provided) and by sorting value set in sorting panel (if sorting value provided)
         * @returns {object|Array}
         */
        items() {
            const t = this.t;
            const tenants = this.categoryIdInt ?
                this.getObjectsByCategoryAZ(this.categoryIdInt) :
                this.getTenantsAZ;

            const filteredTenantsByItem = this.filteredByItem ? tenants.filter(item => item.id !== this.filteredByItem) : tenants;

            if (this.sortingValue && this.sortingValue !== 'All') {
                const valueLength = this.sortingValue.length;
                const selectedItems = [];
                for (let i = 0; i < valueLength; i++) {
                    selectedItems.push(...filteredTenantsByItem.filter((name) => {
                        const firstLetter = t(name, "name").charAt(0);
                        return firstLetter === this.sortingValue.charAt(i);
                    }));
                }
                return selectedItems;
            }
            return tenants;
        },
        /**
         * Detects what letters will render in sorting panel
         * @returns {object|Array}
         */
        sortingPanelButtons() {
            return uniq(this.getTenantsAZ.map((item) => {
                return this.t(item, "name").charAt(0);
            }));
        },
        footerButtonText() {
            return this.$route.name === MOBILE_CARE ? this.$t('ui.close') : this.$t('ui.back')
        },
        /**
         * Detects active letters for index panel
         * * @returns {object|Array}
         */
        indexPanelActiveButtons() {
            return uniq(this.items.map((item) => {
                return this.t(item, "name").charAt(0).toLowerCase();
            }));
        },
        categoryColor(){
            return this.t(this.category, "var_color") ? `var(${this.t(this.category, "var_color")})` : this.t(this.category, "color")
        },
        categoryColorCode() {
            return this.t(this.category, "var_color") ? this.getMapConfigProp(`ui_colors.${this.t(this.category, "var_color")}`, null) : this.t(this.category, "color")
        },
        categoryName(){
            return this.t(this.category, "name")
        },
        categoryDescription(){
            return this.t(this.category, "description")
        },
        categoryIcon(){
            return this.t(this.category, "menuIcon")
        }
    },
    mounted() {
        this.scrollerItemsNumber = Math.ceil( document.querySelector('.mobile-care__scroller').clientHeight / (this.isLandscape ? 38 :67)) + 5;
        this.currentLetterIndexPanel = this.t(this.items[0], 'name').charAt(0).toLowerCase();
    },

    watch: {
        isLandscape(value) {
            if (!value) this.sortingValue = 'All'
        }
    },
    methods: {
        handleClickBack() {
            if (this.$route.name === MOBILE_CATEGORY) this.redirectToHome();
            else this.emitClickBack();
        },
        redirectToHome() {
            const params = {
                name: MOBILE_ROOT
            };
            this.$router.push(params);
        },
        handleTileClick(data) {
            switch(this.$route.name) {
                case MOBILE_GUIDE:
                    this.changeExtremePoint(data);
                    break;
                case MOBILE_CARE:
                default:
                    this.redirectToGuide(data);
                    break;
            }
        },
        emitClickBack() {
            this.$emit('onClickBack', true);
        },
        redirectToGuide(data) {
            const params = {
                name: MOBILE_GUIDE,
                params: {
                    type: 'care'
                },
                query: {
                    to: data.id
                }
            };
            this.$router.push(params);
        },
        changeExtremePoint(data) {
            this.$bus.$emit(GUIDE_NEW_POINT, data.id);
            this.$emit('changeExtremePoint');
        },
        highlightActiveLetter(start, end) {
            const item = this.items[start + (!start ? start : 3)]; //  const item = this.items[start + 3];
            if(end < this.items.length && end > this.scrollerItemsNumber)
                this.currentLetterIndexPanel = this.t(item, 'name').charAt(0).toLowerCase();
        },
        handleClickLetter(letter) {
            const itemIndex = this.items.findIndex( i  => this.t(i,'name',' ').charAt(0).toLowerCase() === letter);
            if(itemIndex !== -1) this.$refs.tilesList.scrollToItem(itemIndex);
        },
        handleClose() {
            this.emitClickBack()
        },
        colorConvert
    }
}
</script>

<style lang="scss">
@import "o-mobile-care";
</style>
